/* import __COLOCATED_TEMPLATE__ from './team-details-row.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type InboxState from 'embercom/services/inbox-state';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import type Session from 'embercom/services/session';
import type TeamSummary from 'embercom/objects/inbox/team-summary';

const INBOX_PRIORITY_PRIMARY = 1;
const COUNT_CLASS_NAMES = {
  atCapacity: 'text-error ',
  notAtCapacity: 'text-muted ',
};

type CountType = 'all' | 'conversations' | 'tickets' | 'channels';

interface Args {
  admin: AdminSummary;
  team: TeamSummary;
  capacityLimit: number;
  adminsBlockable: boolean;
  countType: CountType;
  selectedChannel?: 'email' | 'messenger';
  ticketsAssignmentDisabled: boolean;
  ticketAssignmentLimit: number;
  ticketAssignmentLimitEnabled: boolean;
}

interface Signature {
  Args: Args;
  Element: HTMLDivElement;
}

export default class UnnamedComponent extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare session: Session;

  get adminAssignmentLimit(): number | undefined {
    return this.args.admin.workloadManagementSettings?.assignmentLimit;
  }

  get adminChannelLimit(): number | undefined {
    if (!this.args.selectedChannel) {
      return undefined;
    }
    // null means "no limit set" — normalize to undefined so it renders — and is
    // never treated as a configured 0 (which would falsely flag at-capacity).
    return (
      this.args.admin.workloadManagementSettings?.channelAssignmentLimits?.[
        this.args.selectedChannel
      ] ?? undefined
    );
  }

  get adminChannelCount(): number | undefined {
    if (!this.args.selectedChannel) {
      return undefined;
    }
    // null/missing means "not set" (renders —) and is normalized to undefined;
    // an explicit 0 is preserved and renders as 0.
    return this.args.admin.countMetadata?.channels?.[this.args.selectedChannel] ?? undefined;
  }

  get hasChannelCount(): boolean {
    return this.adminChannelCount !== undefined;
  }

  get hasChannelLimit(): boolean {
    return this.adminChannelLimit !== undefined;
  }

  get assignmentLimit(): number {
    return this.adminAssignmentLimit ?? this.args.capacityLimit;
  }

  get adminTicketAssignmentLimit(): number | undefined {
    return this.args.admin.workloadManagementSettings?.ticketAssignmentLimit;
  }

  get ticketAssignmentLimit(): number {
    return this.adminTicketAssignmentLimit ?? this.args.ticketAssignmentLimit;
  }

  get aboveCapacity() {
    return this.adminInboxCount! >= this.adminCountTypeLimit;
  }

  get aboveTeamCapacity(): boolean {
    // Only show team capacity violations on the ALL tab since team limits
    // are total combined limits (tickets + conversations) and only the ALL tab
    // shows the total count that corresponds to the team limit
    if (this.args.countType !== 'all') {
      return false;
    }

    if (!this.session.workspace.isFeatureEnabled('inbox_assignment_limits')) {
      return false;
    }

    let teamLimit = this.args.team.teamSettings?.assignmentLimit;
    if (!teamLimit) {
      return false;
    }

    return this.teamAssignmentCount >= teamLimit;
  }

  get isPrimaryInbox() {
    return this.args.admin.wlmInboxPriorityLevel === INBOX_PRIORITY_PRIMARY;
  }

  get separateCapacityLimitEnabled(): boolean {
    return !this.args.ticketsAssignmentDisabled && this.args.ticketAssignmentLimitEnabled;
  }

  get adminCountTypeLimit(): number {
    let limitMap: Record<CountType, number> = {
      tickets: this.ticketAssignmentLimit,
      conversations: this.assignmentLimit,
      all: this.assignmentLimit,
      channels: this.adminChannelLimit ?? 0,
    };
    return limitMap[this.args.countType] ?? this.assignmentLimit;
  }

  get adminInboxCount(): number | undefined {
    let countMap: Record<CountType, number | undefined> = {
      tickets: this.adminTicketsCount,
      conversations: this.adminConversationsCount,
      all: this.args.admin.openCount,
      channels: this.adminChannelCount,
    };
    return countMap[this.args.countType];
  }

  get teamAssignmentCount(): number {
    if (this.args.countType !== 'all') {
      return 0;
    }
    let teamAssignmentCount = this.args.admin.getTeamAssignmentCount(this.args.team.id);
    return teamAssignmentCount || 0;
  }

  get shouldShowAtCapacity(): boolean {
    if (!this.args.adminsBlockable || !this.aboveCapacity) {
      return false;
    }

    // Channel limits are independent of ticket-limit separation.
    if (this.args.countType === 'channels') {
      return this.isAtChannelLimit();
    }

    if (this.separateCapacityLimitEnabled) {
      return this.shouldShowCapacityForSeparateLimits();
    }

    return this.shouldShowCapacityForLegacyMode();
  }

  private shouldShowCapacityForSeparateLimits(): boolean {
    let capacityChecks: Record<CountType, boolean> = {
      all: this.isAtBothLimits(),
      tickets: this.isAtTicketLimit(),
      conversations: this.isAtConversationLimit(),
      channels: this.isAtChannelLimit(),
    };
    return capacityChecks[this.args.countType] ?? false;
  }

  private shouldShowCapacityForLegacyMode(): boolean {
    let showConditions: Record<CountType, boolean> = {
      all: !this.args.ticketsAssignmentDisabled,
      conversations: this.args.ticketsAssignmentDisabled,
      tickets: false,
      channels: false,
    };
    return showConditions[this.args.countType] ?? false;
  }

  private isAtBothLimits(): boolean {
    return this.isAtTicketLimit() && this.isAtConversationLimit();
  }

  private isAtTicketLimit(): boolean {
    return this.adminTicketsCount >= this.ticketAssignmentLimit;
  }

  private isAtConversationLimit(): boolean {
    return this.adminConversationsCount >= this.assignmentLimit;
  }

  private isAtChannelLimit(): boolean {
    let limit = this.adminChannelLimit;
    let count = this.adminChannelCount;
    if (limit === undefined || count === undefined) {
      return false;
    }
    return count >= limit;
  }

  get teamCountClassNames() {
    if (this.aboveTeamCapacity) {
      return COUNT_CLASS_NAMES.atCapacity;
    }
    return COUNT_CLASS_NAMES.notAtCapacity;
  }

  get totalCountClassNames() {
    if (this.shouldShowAtCapacity) {
      return COUNT_CLASS_NAMES.atCapacity;
    }
    return COUNT_CLASS_NAMES.notAtCapacity;
  }

  get adminConversationsCount(): number {
    return this.args.admin.countMetadata?.conversations ?? 0;
  }

  get adminTicketsCount(): number {
    return this.args.admin.countMetadata?.tickets ?? 0;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::TeamDetailsRow': typeof UnnamedComponent;
    'inbox2/team-details-row': typeof UnnamedComponent;
  }
}
